// Maps GameEvent[] → animation sequences (docs/08 §6). The only place that animates outcomes.
// The sim already resolved everything; this replays it with juice, then hands control back.
import { B, B_PARAMS } from '../../content/balance';
import { formatMult, formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { audio } from '../../audio/audio';
import { Decimal } from '../../sim/decimal';
import { tierAtLeast, TIER_ORDER } from '../../sim/scoring';
import { hungerActive, MOOD_FACE, moodOf } from '../../sim/hunger';
import { computeModifiers } from '../../sim/modifiers';
import type { Cell, Command, Expression, GameEvent, GameState, SizeId, SpinSnapshot, WinTier } from '../../sim/types';
import { getGame, getUI, onEvents, pushLog, setUI, store, toast } from '../../state/store';
import { toLogical, type GameApp, LOGICAL_W } from '../app';
import type { Fx } from '../fx/fx';
import type { Particles } from '../fx/Particles';
import type { Popups } from '../fx/Popups';
import { PATTERN_COLOR, type MachineScene } from '../machine/MachineScene';
import { clock } from './clock';
import { juice } from '../juice';
import { platform } from '../../platform/platform';

export interface SceneHooks {
  bonusOpen?(): void;
  bonusUpdate?(): Promise<void>;
  bonusClose?(payout: string, voided: boolean): Promise<void>;
  nodeBought?(id: string): Promise<void>;
  molted?(): Promise<void>;
}

interface Batch {
  id: number;
  events: GameEvent[];
  cmd: Command;
  prev: GameState;
}

const LOW_VOICE: (Expression | undefined)[] = ['hungry', 'ravenous', 'starving'];

/** Receipt-printer tips raised by the presentation (the sim raises the rest). */
function tipOnce(key: string): void {
  const g = getGame();
  if (!g.meta.settings.tips || getUI().log.some((l) => l.key === `tip.${key}`)) return;
  pushLog({ kind: 'tip', key: `tip.${key}` });
}
/** Act weight for the music decay (Acts I–IV by machine size). */
const ACT_DECAY: Record<SizeId, number> = { bandit: 0, cabinet: 0, wide: 1 / 3, grand: 1 / 3, tall: 2 / 3, cathedral: 1 };

const COUNT_MS: Record<WinTier, number> = { none: 0, win: 600, big: 1200, mega: 2000, epic: 3500, legendary: 6000, gorge: 5000 };

export class EventPlayer {
  private queue: Batch[] = [];
  private running = false;
  private nextId = 1;
  private skipThrough = 0;
  private shownTier = new Map<number, WinTier>();
  private lastSnapshot: SpinSnapshot | null = null;
  private hitStep = 0;
  /** The face the machine returns to between reactions (its current mood). */
  private moodFace: Expression = 'neutral';
  /** Instances eaten this spin: their cells stay faded until the next spin. */
  private eatenUids = new Set<number>();
  leverPulled = false;
  hooks: SceneHooks = {};

  constructor(
    private ga: GameApp,
    private machine: MachineScene,
    private fx: Fx,
    private particles: Particles,
    private popups: Popups,
  ) {
    onEvents((events, cmd, prev) => {
      if (events.length) this.enqueue(events, cmd, prev);
      else this.machine.sync(getGame());
    });
  }

  get busy(): boolean {
    return this.running;
  }

  enqueue(events: GameEvent[], cmd: Command, prev: GameState): void {
    this.queue.push({ id: this.nextId++, events, cmd, prev });
    if (!this.running) void this.run();
  }

  /** Finish everything queued so far instantly (docs/08 §6: PULL during a win fast-forwards). */
  skipAll(): void {
    if (!this.running) return;
    this.skipThrough = this.nextId - 1;
    clock.skip();
  }

  private async run(): Promise<void> {
    this.running = true;
    clock.speed = juice.speed();
    setUI({ busy: true });
    while (this.queue.length) {
      const b = this.queue.shift()!;
      if (b.id > this.skipThrough) clock.endSkip();
      else clock.skip();
      const ui = getUI();
      if (ui.displayedCoins === null && b.prev.run) setUI({ displayedCoins: b.prev.run.coins.toString() });
      if (ui.displayedSouls === null) setUI({ displayedSouls: b.prev.meta.souls });
      for (const e of b.events) {
        try {
          await this.handle(e, b);
        } catch (err) {
          console.error('event failed', e, err);
        }
      }
    }
    clock.endSkip();
    this.running = false;
    this.machine.animating = false;
    setUI({ busy: false, displayedCoins: null, displayedSouls: null });
    this.machine.sync(getGame());
    this.applyMood(getGame());
  }

  /**
   * Mood → idle face, red LEDs when STARVING, the hunger vignette at RAVENOUS+, and the music's
   * decay (docs/07 §4.1: Hunger 0.5, Act 0.3, Doom 0.2).
   */
  applyMood(game: GameState): void {
    const run = game.run;
    const mods = computeModifiers(game.meta, run);
    const active = !!run && hungerActive(mods);
    const mood = active ? moodOf(run, mods) : 'content';
    const hunger = active ? run.hunger : 0;
    const face = MOOD_FACE[mood];
    if (face !== this.moodFace) {
      this.moodFace = face;
      if (this.machine.display.hasFace && !this.running) this.machine.display.setExpression(face);
    }
    this.machine.display.setRed(mood === 'starving');
    this.machine.display.setPortrait(run?.phase === 'consumed' ? null : (run?.window.reckoning ?? null));
    this.fx.setVignette(active && hunger >= B.MOOD_RAVENOUS, hunger >= B.HUNGER_MAX);
    const act = ACT_DECAY[run?.size ?? 'bandit'];
    // Act layers: II (4×3/5×3) vibraphone, III (5×4) organ, IV (5×5) organ and choir.
    audio.music.setAct(Math.round(act * 3));
    const doom = run && mods.features.has('eyes') ? run.doom / Math.max(1, mods.doomMax) : 0;
    // After UNPLUGGED the jazz plays clean until the next run.
    if (!getUI().cinematic) audio.music.setDecay(0.5 * (hunger / B.HUNGER_MAX) + 0.3 * act + 0.2 * doom);
  }

  private coinAnchor(sel = '[data-anchor="coins"]'): { x: number; y: number } {
    const el = document.querySelector(sel);
    if (!el) return { x: 60, y: 90 };
    const r = el.getBoundingClientRect();
    return toLogical(this.ga, r.left + r.width / 2, r.top + r.height / 2);
  }

  private async handle(e: GameEvent, b: Batch): Promise<void> {
    const m = this.machine;
    switch (e.e) {
      case 'RUN_START': {
        this.shownTier.clear();
        setUI({ stage: 'play' });
        audio.music.start();
        m.sync(getGame());
        return;
      }
      case 'IT_WAKES':
        return this.itWakes();
      case 'WINDOW_START': {
        setUI({ stage: 'play' });
        {
          const g = getGame();
          const mods = computeModifiers(g.meta, g.run);
          platform.presence(e.n, g.run && hungerActive(mods) ? t(`mood.${moodOf(g.run, mods)}`) : '');
        }
        m.clearPatterns();
        this.popups.clear();
        m.sync(getGame());
        audio.play('printer');
        await clock.wait(250);
        return;
      }
      case 'SPIN_START': {
        m.animating = true;
        const dark = computeModifiers(getGame().meta, getGame().run).darkReels;
        this.eatenUids.clear();
        m.clearPatterns();
        this.popups.clear();
        this.hitStep = 0;
        if (!this.leverPulled) void m.lever.animatePull();
        this.leverPulled = false;
        if (e.perfect) {
          const l = m.lever;
          void this.popups.float(t('ui.perfect'), l.x, l.y - 70, 'white');
          this.fx.shake(2, 200);
        }
        m.reels.forEach((r, i) => {
          if (!e.held[i]) r.startSpin();
          r.setDark(dark && !e.held[i]);
        });
        audio.startLoop('whirr');
        await clock.wait(260);
        return;
      }
      case 'REEL_STOP': {
        const r = m.reels[e.reel];
        if (!r) return;
        const held = (b.events.find((x) => x.e === 'SPIN_START') as { held?: boolean[] } | undefined)?.held?.[e.reel];
        if (held) {
          r.setColumn(e.column);
          return;
        }
        await clock.wait(B.REEL_STAGGER_MS);
        if (e.anticipation) {
          r.setGlow(true);
          audio.startLoop('drone');
          m.display.setExpression('surprised');
        }
        await r.stop(e.column, e.anticipation ? 600 : 0);
        r.setDark(false);
        if (e.anticipation) {
          r.setGlow(false);
          audio.stopLoop('drone');
        }
        audio.play('reelStop', { pitch: e.reel });
        this.fx.shake(1, 90);
        if (e.reel === m.reels.length - 1) audio.stopLoop('whirr');
        return;
      }
      case 'FATE_PASS': {
        const r = m.reels[e.reel];
        if (!r) return;
        audio.play('fate');
        const top = m.reelTop(e.reel);
        this.particles.burst('sparkle', { x: top.x, y: top.y + r.cellSize / 2 }, 8, 30, 500);
        await r.twitch(e.column);
        return;
      }
      case 'SECOND_CHANCE': {
        void this.popups.float(t('ui.secondChance'), m.gridCenter().x, m.layout.window.y - 6, 'mint');
        await Promise.all(m.reels.map((r, i) => r.twitch(e.columns[i]!)));
        return;
      }
      case 'PITY': {
        void this.popups.float(t('ui.status.pity', { n: e.luck }), m.gridCenter().x, m.layout.window.y - 14, 'mint');
        return;
      }
      case 'SPINS_ADDED': {
        const a = this.coinAnchor('[data-anchor="spins"]');
        void this.popups.float(t('ui.spinsAdded', { n: e.n }), a.x, a.y, 'mint');
        audio.play('sparkle');
        return;
      }
      case 'SYMBOL_TRANSFORMED': {
        if (e.cell) {
          const p = m.cellCenter(e.cell);
          this.particles.burst('sparkle', p, 6, 24, 400);
          audio.play('pop');
        }
        return;
      }
      case 'SYMBOL_EFFECT': {
        const p = m.cellCenter(e.cell);
        if (e.kind === 'grow' && e.delta) void this.popups.float(`+${e.delta}`, p.x, p.y - 8, 'gold');
        return;
      }
      case 'SYMBOL_DESTROYED': {
        const snap = this.lastSnapshot;
        if (snap) {
          snap.uids.forEach((col, reel) =>
            col.forEach((uid, row) => {
              if (uid !== e.uid) return;
              const p = m.cellCenter({ reel, row });
              this.particles.burst('coin', p, 6, 40, 400);
              void this.popups.float(t('ui.destroyed'), p.x, p.y, 'red');
            }),
          );
        }
        audio.play('crunch');
        await clock.wait(200);
        return;
      }
      case 'SYMBOL_EATEN':
        this.eatenUids.add(e.uid);
        return this.eaten(e.cells);
      case 'SYMBOL_FED': {
        audio.play('chomp', { volume: 0.7 });
        audio.play('crunch', { pitch: 2 });
        const a = this.coinAnchor('[data-anchor="feedHunger"]');
        void this.popups.float(t('ui.feed.amount', { n: Math.round(e.amount) }), a.x, a.y, 'red');
        return;
      }
      case 'HUNGER': {
        this.applyMood(getGame());
        if (e.mood !== e.prevMood) {
          if (e.mood === 'ravenous' || e.mood === 'starving') {
            audio.play('rumble', { pitch: -8, volume: 0.6 });
            this.fx.shake(2, 300);
          }
          if (this.machine.display.hasFace) this.machine.display.setExpression(MOOD_FACE[e.mood]);
        }
        return;
      }
      case 'SPIN_RESOLVED':
        return this.resolved(e.snapshot, e.par);
      case 'NUDGE_OPEN': {
        const snap = this.lastSnapshot;
        if (snap?.nearMiss.length) {
          audio.play('nearMiss');
          await Promise.all(
            snap.nearMiss.map((nm) => m.reels[nm.reel]?.blinkPeek(nm.dir === 1 ? 'top' : 'bottom') ?? Promise.resolve()),
          );
        }
        return;
      }
      case 'NUDGED': {
        m.clearPatterns();
        this.popups.clear();
        audio.play('ratchet', { pitch: 4 });
        if (e.free) void this.popups.float(t('ui.nudge.free'), m.reelTop(e.reel).x, m.layout.window.y - 6, 'mint');
        await m.reels[e.reel]?.slide(e.column, e.dir, e.steps);
        return;
      }
      case 'COINS_BANKED':
        return this.banked(e.amount, e.total);
      case 'HOLD_CHANGED':
      case 'CHARGES':
        m.sync(getGame());
        return;
      case 'BONUS_TRIGGER': {
        audio.play('bonusStart');
        m.display.setExpression('surprised');
        await this.popups.banner(t(`ui.bonus.${e.mode}`), LOGICAL_W / 2, 150, 'mint', 2, 700);
        setUI({ stage: 'bonus' });
        audio.music.setMode(e.mode);
        this.hooks.bonusOpen?.();
        return;
      }
      case 'BONUS_UPDATE':
        await this.hooks.bonusUpdate?.();
        return;
      case 'BONUS_END': {
        await this.hooks.bonusClose?.(e.payout, e.voided);
        audio.music.setMode(null);
        setUI({ stage: 'play' });
        if (!e.voided && e.payout !== '0') {
          const from = m.gridCenter();
          const to = this.coinAnchor();
          const coins = getGame().run?.coins.toString() ?? '0';
          await this.particles.flight('coin', from, to, 30, 900);
          setUI({ displayedCoins: coins });
        }
        return;
      }
      case 'TRIBUTE_PAID': {
        audio.play('printer');
        setUI({ displayedCoins: e.coinsLeft });
        await clock.wait(250);
        audio.play('register');
        audio.play('stamp');
        this.fx.shake(2, 200);
        const a = this.coinAnchor('[data-anchor="tribute"]');
        void this.popups.float(t('ui.paid'), a.x, a.y, 'red', 2, 900);
        const s = this.coinAnchor('[data-anchor="soulsPending"]');
        await this.particles.flight('soul_flame', a, s, Math.min(12, e.souls + 2), 700);
        await clock.wait(400);
        return;
      }
      case 'INTERMISSION_OPEN':
        m.clearPatterns();
        this.popups.clear();
        setUI({ stage: 'intermission' });
        return;
      case 'CONSUMED':
        if (b.prev.run?.flags.endless) platform.endlessScore(getGame().meta.lifetime.bestEndless);
        return this.consumed(e.souls, b.prev.meta.souls);
      case 'NODE_BOUGHT': {
        audio.play('choir', { pitch: (e.level - 1) * 2 });
        setUI({ displayedSouls: getGame().meta.souls });
        await this.hooks.nodeBought?.(e.id);
        return;
      }
      case 'SAY': {
        const text = t(e.key, e.params);
        // At high Doom the voice sometimes slips into lowercase (a presentation flourish, not an outcome).
        const run = getGame().run;
        const glitch = e.channel !== 'receipt' && !!run && run.doom >= B.DOOM_GLITCH && Math.random() < B.DOOM_GLITCH_CHANCE;
        pushLog({ kind: e.channel === 'receipt' ? 'print' : 'say', key: e.key, params: e.params, glitch });
        if (glitch) {
          m.display.setExpression('glitch');
          await clock.wait(180);
        }
        if (e.channel === 'receipt') {
          audio.play('printer', { volume: 0.5 });
          return;
        }
        if (e.expression) {
          m.display.setExpression(e.expression);
          setUI({ expression: e.expression });
        }
        setUI({ say: { key: e.key, params: e.params, expression: e.expression, id: Date.now() } });
        const pitch = LOW_VOICE.includes(e.expression!) ? -3 : e.expression === 'laughing' || e.expression === 'pleased' ? 2 : 0;
        void m.display.say(text, pitch);
        return;
      }
      case 'TIP':
        pushLog({ kind: 'tip', key: `tip.${e.key}` });
        audio.play('printer', { volume: 0.4 });
        return;
      case 'EXPRESSION':
        m.display.setExpression(e.expression);
        return;
      case 'DRAFT_PICKED':
      case 'CHARM_BOUGHT':
      case 'HYMN_BOUGHT':
        audio.play('register', { volume: 0.6 });
        return;
      case 'CHARM_SOLD':
      case 'DRAFT_SKIPPED':
      case 'SHOP_UPDATE':
      case 'DRAFT_OFFER':
        return;
      case 'CHARM_TRIGGER': {
        const a = this.coinAnchor();
        if (e.amount) void this.popups.float(`+${formatNumber(e.amount)}`, a.x, a.y, 'gold');
        audio.play('coin');
        return;
      }
      default:
        return this.handleAct2(e);
    }
  }

  /** Act II events: Reckonings, hexes, deals, growth, bonuses' riders, Molt (docs/07 §2). */
  private async handleAct2(e: GameEvent): Promise<void> {
    const m = this.machine;
    const disp = m.layout.display;
    const dispMid = { x: disp.x + disp.w / 2, y: disp.y + disp.h / 2 };
    switch (e.e) {
      case 'RECKONING_START': {
        audio.play('rumble', { pitch: -4 });
        audio.play('bonusStart', { pitch: -5 });
        this.fx.shake(3, 400);
        if (!juice.reduceFlashing()) this.fx.flash('crimson', 0.35, 160);
        m.display.setPortrait(e.id);
        await this.popups.banner(`${t('ui.reckoning.title')}: ${t(`collector.${e.id}.name`)}`, LOGICAL_W / 2, 150, 'red', 2, 900);
        return;
      }
      case 'RECKONING_BEATEN': {
        audio.play('register');
        audio.play('choir', { pitch: 4 });
        this.particles.burst('soul_flame', dispMid, 16, 60, 900);
        await this.popups.banner(t('ui.reckoning.beaten', { name: t(`collector.${e.id}.name`), souls: e.souls }), LOGICAL_W / 2, 150, 'lavender', 1, 900);
        m.display.setPortrait(null);
        return;
      }
      case 'RULE_NEXT': {
        const id = e.rules[0];
        if (id) void this.popups.float(t('ui.reckoning.nextRule', { name: t(`collector.${id}.name`) }), dispMid.x, disp.y + disp.h + 6, 'red', 1, 1400);
        return;
      }
      case 'TWINS_PAID': {
        audio.play('register');
        audio.play('stamp');
        setUI({ displayedCoins: e.coinsLeft });
        const a = this.coinAnchor('[data-anchor="tribute"]');
        await this.popups.float(t('ui.reckoning.twinsPaid'), a.x, a.y, 'red', 1, 900);
        return;
      }
      case 'HEX_APPLIED': {
        audio.play('stamp', { pitch: -3 });
        this.fx.shake(2, 250);
        const a = this.coinAnchor('[data-anchor="hexes"]');
        void this.popups.float(t(`hex.${e.id}.name`), a.x, a.y, 'red', 1, 1000);
        m.sync(getGame());
        await clock.wait(250);
        return;
      }
      case 'HEX_REMOVED': {
        if (e.by === 'time') return;
        audio.play('sparkle');
        const a = this.coinAnchor('[data-anchor="hexes"]');
        void this.popups.float(`−${t(`hex.${e.id}.name`)}`, a.x, a.y, 'mint', 1, 800);
        return;
      }
      case 'CLEANSE': {
        audio.play('choir', { pitch: 7 });
        audio.play('sparkle');
        this.fx.flash('lavender', 0.4, 200);
        this.particles.burst('sparkle', m.gridCenter(), 40, 90, 900);
        await this.popups.banner(t('ui.cleanse'), LOGICAL_W / 2, 140, 'lavender', 2, 600);
        return;
      }
      case 'DEAL_OFFER':
        audio.play('printer');
        return;
      case 'DEAL_ACCEPTED':
        audio.play('sign');
        if (!juice.reduceFlashing()) this.fx.flash('crimson', 0.25, 120);
        return;
      case 'DEAL_REFUSED':
        audio.play('error');
        return;
      case 'MACHINE_GROWS':
        return this.grows(e.from);
      case 'GAMBLE_OFFER':
        audio.play('bonusStart', { pitch: 3, volume: 0.6 });
        tipOnce('gamble');
        return;
      case 'NIGHT_SHIFT': {
        m.sync(getGame());
        if (e.left > 0) void this.popups.float(t('ui.night.left', { n: e.left }), dispMid.x, disp.y + disp.h + 6, 'lavender', 1, 900);
        return;
      }
      case 'CHARM_GAINED':
        audio.play('register');
        toast(t('ui.charm.gainedName', { name: t(`charm.${e.id}.name`) }));
        return;
      case 'CHARM_LOST':
        audio.play('crunch');
        toast(t('ui.charm.lostName', { name: t(`charm.${e.id}.name`) }));
        return;
      case 'SHARD_GAINED':
        audio.play('relic');
        toast(t('ui.shard.gained', { n: e.shards }));
        return;
      case 'RELIC_GAINED':
        audio.play('relic');
        toast(t('ui.relic.gained', { name: t(`relic.${e.id}.name`) }));
        return;
      case 'PAGE_FOUND':
        audio.play('printer');
        toast(t('ui.page.found'));
        return;
      case 'PLUG_FOUND':
        audio.play('relic', { pitch: -5 });
        audio.play('static', { volume: 0.4 });
        toast(t('ui.plug.found', { name: t(`item.${e.id}.name`) }));
        return;
      case 'LAST_MEAL': {
        audio.play('chomp');
        this.fx.shake(4, 400);
        await this.popups.banner(t('ui.lastMeal', { charm: t(`charm.${e.charm}.name`) }), LOGICAL_W / 2, 150, 'red', 1, 1200);
        return;
      }
      case 'MOLTED': {
        audio.play('static');
        audio.play('rumble', { pitch: -6 });
        this.fx.shake(6, 900);
        this.fx.flash('white', 0.9, 160);
        await clock.wait(500);
        audio.play('choir', { pitch: -5 });
        await this.popups.banner(t('ui.molt.done', { form: t(`ui.form.${e.form}`) }), LOGICAL_W / 2, 150, 'pinkLilac', 2, 1400);
        await this.hooks.molted?.();
        return;
      }
      case 'SYMBOL_GROWN':
        return;
      default:
        return this.handleAct3(e);
    }
  }

  /** Act III–IV events: Doom, Devour, the payphone, the endings (docs/07 §2, docs/06 §7). */
  private async handleAct3(e: GameEvent): Promise<void> {
    const m = this.machine;
    const disp = m.layout.display;
    const dispMid = { x: disp.x + disp.w / 2, y: disp.y + disp.h / 2 };
    switch (e.e) {
      case 'DOOM': {
        if (!e.noticed) return;
        audio.play('wake', { pitch: -6, volume: 0.7 });
        m.display.setExpression('noticed');
        this.fx.shake(2, 250);
        await this.popups.float(t('ui.noticed'), dispMid.x, disp.y + disp.h + 6, 'red', 1, 900);
        m.display.setExpression(this.moodFace);
        return;
      }
      case 'DEVOUR':
        return this.devour(e.kind);
      case 'PHONE_RING':
        audio.play('phone');
        toast(t('ui.phone.ring'));
        return;
      case 'PHONE_CALL':
        setUI({ call: e.script });
        return;
      case 'ACHIEVEMENT':
        platform.unlock(e.id);
        audio.play('relic', { pitch: 7 });
        toast(t('ui.ach.unlocked', { name: t(`ach.${e.id}.name`) }));
        return;
      case 'ENDING_CHOICE':
        audio.play('choir', { pitch: -7 });
        m.display.setExpression('adoring');
        await clock.wait(400);
        setUI({ stage: 'ending' });
        return;
      case 'ENDING':
        setUI({ cinematic: { ending: e.ending, first: e.first }, stage: 'ending' });
        return;
      case 'REMEMBERED':
        if (e.choice === 'endless') {
          audio.play('laugh');
          m.display.setExpression('laughing');
          await this.popups.banner(t('ui.endless.badge'), LOGICAL_W / 2, 150, 'red', 3, 1000);
          setUI({ stage: 'intermission' });
        } else setUI({ stage: 'consumed' });
        return;
      default:
        return;
    }
  }

  /** Devour (docs/07 §2): the lights cut, a red chomp over the grid, the payout drains into the mouth. */
  private async devour(kind: 'blocked' | 'devour' | 'golden' | 'miracle'): Promise<void> {
    const m = this.machine;
    const mid = m.gridCenter();
    if (kind === 'blocked') {
      audio.play('choir', { pitch: 9, volume: 0.6 });
      this.particles.burst('sparkle', mid, 18, 60, 700);
      await this.popups.float(t('ui.devour.blocked'), mid.x, mid.y, 'gold', 2, 800);
      return;
    }
    if (kind === 'miracle') {
      audio.play('choir', { pitch: 5 });
      audio.play('sparkle');
      this.fx.flash(juice.reduceFlashing() ? 'lavender' : 'white', 0.6, 200);
      this.particles.burst('sparkle', mid, 50, 110, 1100);
      await this.popups.banner(t('ui.devour.miracle'), LOGICAL_W / 2, 150, 'lavender', 2, 1000);
      return;
    }
    m.display.setExpression('devouring');
    audio.play('devour');
    if (juice.reduceFlashing()) await this.fx.blackout(200, 150, 200);
    else await this.fx.blackout(60, 240, 0);
    audio.play('chomp', { pitch: -6 });
    this.fx.shake(6, 500);
    this.fx.flash(kind === 'golden' ? 'gold' : 'blood', 0.9, 400);
    if (kind === 'golden') this.particles.burst('coin', mid, 40, 100, 900);
    await this.popups.banner(kind === 'golden' ? t('ui.devour.golden', B_PARAMS) : t('ui.devour'), LOGICAL_W / 2, mid.y, kind === 'golden' ? 'gold' : 'red', 3, 900);
    m.display.setExpression(this.moodFace);
  }

  /** It Stretches / It Widens: the cabinet cracks and grows around new reels. */
  private async grows(from: SizeId): Promise<void> {
    const m = this.machine;
    setUI({ stage: 'waking' });
    const g = getGame();
    const size = g.run?.size ?? from;
    m.size = null;
    m.build(from, m.peek);
    m.sync(g);
    await clock.wait(400);
    audio.play('rumble');
    for (let i = 0; i < 3; i++) {
      this.fx.shake(3 + i * 2, 300);
      audio.play('crunch', { pitch: -8 + i * 3 });
      await clock.wait(380);
    }
    this.fx.flash('white', 0.8, 120);
    m.size = null;
    m.build(size, m.peek);
    m.sync(g);
    audio.play('wake');
    if (size === 'tall') {
      // The Act III reveal (docs/07 §1.3): the camera pulls back to 1× and the tarps fall.
      const c = m.gridCenter();
      m.scale.set(1.35);
      m.position.set(-c.x * 0.35, -c.y * 0.35);
      await clock.wait(300);
      await Promise.all([clock.tween(m.scale, 1600, { x: 1, y: 1, ease: 'power2.inOut' }), clock.tween(m.position, 1600, { x: 0, y: 0, ease: 'power2.inOut' })]);
      audio.play('static', { volume: 0.5 });
      await m.arcade.dropTarps();
    } else if (size === 'cathedral') {
      // The Cathedral: organ chords join the jazz.
      for (const p of [0, 3, 7]) {
        audio.play('organ', { pitch: p });
        this.fx.shake(2, 300);
        await clock.wait(450);
      }
    }
    m.scale.set(1);
    m.position.set(0, 0);
    await this.popups.banner(t(size === 'tall' ? 'ui.grow.tall' : size === 'cathedral' ? 'ui.grow.cathedral' : 'ui.grow.title'), LOGICAL_W / 2, 150, 'gold', 2, 800);
    setUI({ stage: 'play' });
  }

  private async resolved(snap: SpinSnapshot, parStr: string): Promise<void> {
    const m = this.machine;
    const prevSnap = this.lastSnapshot;
    this.lastSnapshot = snap;
    audio.stopLoop('whirr');
    m.animating = false;
    // on-land transforms change what's visible
    snap.grid.forEach((col, reel) => {
      const changed = m.reels[reel]?.replaceRows(col) ?? [];
      for (const row of changed) m.reels[reel]?.pulse(row);
      const eaten = snap.uids[reel]!.flatMap((uid, row) => (this.eatenUids.has(uid) ? [row] : []));
      m.reels[reel]?.dimRows(eaten);
    });
    const notation = getGame().meta.settings.notation;
    const renudge = prevSnap?.index === snap.index;
    const stepMs = renudge ? 120 : 250;
    let i = 0;
    for (const h of snap.hits) {
      m.drawPattern(h.cells, PATTERN_COLOR[h.pattern]);
      for (const c of h.cells) m.reels[c.reel]?.pulse(c.row);
      const pts = h.cells.map((c) => m.cellCenter(c));
      const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
      const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length;
      const label = `${formatNumber(h.valueSum, notation)}×${formatMult(h.mult)}`;
      void this.popups.float(label, cx, cy - 6, 'gold', 1, 900);
      audio.play('patternBlip', { pitch: this.hitStep++ * 2 });
      pushLog({
        kind: 'print',
        key: 'log.pattern',
        params: { pattern: t(`pattern.${h.pattern}`), value: formatNumber(h.value, notation) },
      });
      i++;
      await clock.wait(stepMs);
    }
    for (const o of snap.off) {
      const p = m.cellCenter(o.cell);
      void this.popups.float(`+${formatNumber(o.value, notation)}`, p.x, p.y - 6, 'gold');
      m.reels[o.cell.reel]?.pulse(o.cell.row);
      audio.play('coin', { pitch: this.hitStep++ });
      await clock.wait(stepMs * 0.6);
    }
    for (const x of snap.xMults) {
      if (x.x === 1) continue;
      const c = m.gridCenter();
      void this.popups.float(`×${formatMult(x.x)}`, c.x, c.y + 10, 'lavender', 2);
      audio.play('kachunk');
      await clock.wait(200);
    }
    if (i === 0 && snap.off.length === 0) {
      m.display.setExpression(this.moodFace);
      return;
    }
    const prevTier = this.shownTier.get(snap.index) ?? 'none';
    if (TIER_ORDER.indexOf(snap.tier) > TIER_ORDER.indexOf(prevTier)) {
      this.shownTier.set(snap.index, snap.tier);
      await this.tierFx(snap.tier, snap.payout, new Decimal(parStr));
    }
  }

  private async tierFx(tier: WinTier, payout: Decimal, _par: Decimal): Promise<void> {
    const m = this.machine;
    const c = m.gridCenter();
    const amount = formatNumber(payout, getGame().meta.settings.notation);
    if (tier === 'win' || tier === 'none') {
      void this.popups.float(`+${amount}`, c.x, m.layout.window.y + m.layout.window.h + 2, 'gold', 1, 700);
      return;
    }
    if (tier === 'gorge') return this.gorge(amount);
    const label = t(`ui.tier.${tier}`);
    audio.play('bigHit');
    this.particles.burst('coin', c, tier === 'big' ? 40 : 70, 90, 900);
    if (tierAtLeast(tier, 'mega')) {
      this.fx.shake(6, 600);
      this.fx.punch(this.ga.world, 0.05);
      audio.music.duck(-6, 2000);
      audio.play('swell');
    }
    if (tierAtLeast(tier, 'epic')) {
      m.display.setExpression('laughing');
      audio.play('laugh');
      await clock.wait(150); // freeze frame
      this.fx.flash('white', 0.5, 90);
    }
    if (tier === 'legendary') {
      this.particles.rain('coin', LOGICAL_W, 120, 2500);
      audio.play('jingle');
      this.fx.flash('gold', 0.4, 200);
    }
    const scale = tier === 'big' ? 2 : 3;
    const hold = tier === 'big' ? 500 : tier === 'mega' ? 900 : 1400;
    await this.popups.banner(`${label}  +${amount}`, LOGICAL_W / 2, 120, tier === 'big' ? 'gold' : tier === 'mega' ? 'orange' : 'lavender', scale, hold);
  }

  private async gorge(amount: string): Promise<void> {
    const m = this.machine;
    m.display.setExpression('devouring');
    audio.play('gorge');
    audio.music.duck(-9, 3000);
    await this.fx.blackout(150, 250, 250);
    this.fx.shake(8, 800);
    this.particles.burst('coin', m.gridCenter(), 120, 150, 1400);
    await this.popups.banner(`${t('ui.tier.gorge')}  +${amount}`, LOGICAL_W / 2, 120, 'red', 3, 1500);
  }

  private async banked(amountStr: string, totalStr: string): Promise<void> {
    const amount = new Decimal(amountStr);
    if (amount.lte(0)) {
      setUI({ displayedCoins: totalStr });
      return;
    }
    const snap = this.lastSnapshot;
    const tier = snap ? (this.shownTier.get(snap.index) ?? snap.tier) : 'win';
    const count = Math.max(4, Math.min(60, Math.round(Math.log2(amount.toNumber() + 1) * 6)));
    const from = this.machine.gridCenter();
    const to = this.coinAnchor();
    const start = new Decimal(getUI().displayedCoins ?? totalStr).sub(0);
    const total = new Decimal(totalStr);
    const ms = Math.max(500, COUNT_MS[tier] * 0.5);
    let landed = 0;
    await this.particles.flight('coin', from, to, count, ms, () => {
      landed++;
      const f = landed / count;
      const shown = start.add(total.sub(start).mul(f)).floor();
      setUI({ displayedCoins: shown.toString() });
      if (landed % 2 === 0) audio.play('countTick', { pitch: Math.min(12, Math.floor(f * 12)), volume: 0.5 });
    });
    setUI({ displayedCoins: totalStr });
  }

  private async consumed(souls: number, soulsBefore: number): Promise<void> {
    const m = this.machine;
    audio.music.tapeStop();
    audio.play('rumble');
    if (m.display.hasFace) m.display.setExpression('devouring');
    await clock.wait(600);
    audio.play('chomp');
    this.fx.shake(6, 500);
    await this.fx.blackout(500, 600, 0);
    // the soul drifts off
    const from = { x: LOGICAL_W / 2, y: 200 };
    const to = this.coinAnchor('[data-anchor="souls"]');
    await this.particles.flight('soul_flame', from, to, Math.min(30, souls + 3), 1200);
    setUI({ displayedSouls: soulsBefore + souls, stage: 'consumed' });
    await this.fx.fadeTo('ink', 0, 300);
    m.display.setExpression(this.moodFace);
  }

  /** The machine eats a visible symbol: chomp on each of its cells (docs/04 §12). */
  private async eaten(cells: Cell[]): Promise<void> {
    const m = this.machine;
    if (m.display.hasFace) m.display.setExpression('devouring');
    audio.play('chomp');
    this.fx.shake(4, 300);
    for (const c of cells) {
      const p = m.cellCenter(c);
      m.reels[c.reel]?.pulse(c.row);
      m.reels[c.reel]?.dimRows(cells.filter((x) => x.reel === c.reel).map((x) => x.row));
      this.particles.burst('coin', p, 10, 50, 500);
      void this.popups.float(t('ui.chomp'), p.x, p.y - 4, 'red', 2, 700);
    }
    if (!juice.reduceFlashing()) this.fx.flash('blood', 0.25, 120);
    await clock.wait(450);
    if (m.display.hasFace) m.display.setExpression(this.moodFace);
  }

  private async itWakes(): Promise<void> {
    const m = this.machine;
    setUI({ stage: 'waking' });
    m.build('bandit', 1);
    m.sync(store.getState().game);
    await clock.wait(500);
    audio.play('rumble');
    for (let i = 0; i < 3; i++) {
      this.fx.shake(3 + i * 2, 300);
      audio.play('crunch', { pitch: -6 + i * 3 });
      await clock.wait(450);
    }
    this.fx.flash('white', 0.9, 120);
    const g = getGame();
    const size = g.run?.size ?? 'cabinet';
    m.size = null;
    m.build(size, 1);
    m.sync(g);
    audio.play('wake');
    m.display.blank();
    for (let i = 0; i < 5; i++) {
      await clock.wait(90 + Math.random() * 120);
      if (i % 2 === 0) m.display.showFace('sleeping');
      else m.display.blank();
    }
    m.display.showFace('sleeping');
    await clock.wait(700);
    m.display.showFace('surprised');
    await clock.wait(500);
    m.display.showFace('neutral');
    setUI({ stage: 'play' });
  }
}
